import type { Maybe } from 'graphql/jsutils/Maybe';
import type { ReactElement } from 'react';

import { memo } from 'react';
import styled, { css } from 'styled-components';

import { SiteType } from '~/utilities/graphql/codegen';
import type { NavigationItem } from '~/utilities/graphql/codegen';
import { getSiteStyles } from '~/utilities/helpers';

const S = {
  ParentNode: styled.button`
    ${({ theme }) => css`
      padding: 0;
      color: ${theme.colors.content.primary};
      text-transform: uppercase;
      min-width: 0;
      margin: 0 12px;
      padding-top: 2px;
      font-size: 14px;
      font-weight: 500;
      line-height: 51px;
      border: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      cursor: pointer;

      &:first-of-type {
        margin-left: 0;
        padding-left: 8px;
        text-align: right;
        grid-column-start: 2;
      }

      &:nth-of-type(2n) {
        margin-right: 0;
        padding-right: 8px;
        text-align: left;
      }

      &:hover,
      &:focus,
      &.active {
        text-decoration: underline;
        text-decoration-thickness: 2px;
        text-underline-offset: 3px;

        ${getSiteStyles(theme.siteType, {
          [SiteType.Outlet]: css`
            color: ${theme.colors.background.component.button.primary.hover};
          `,
        })}
      }
    `}
  `,
};

type ParentNodesProps = {
  navigation: Maybe<NavigationItem>[];
  parentNode: { [k: number]: boolean };
  onParentNodeClick: (i: number) => void;
};

const ParentNodesComponent = ({
  navigation,
  parentNode,
  onParentNodeClick,
}: ParentNodesProps): ReactElement => (
  <>
    {navigation.map((node, i) => (
      <S.ParentNode
        key={node?.uid ?? i}
        role="tab"
        id={`inpage-tab-${i}`}
        aria-selected={!!parentNode[i]}
        aria-controls={`inpage-tabpanel-${i}`}
        className={parentNode[i] ? 'active' : ''}
        onClick={() => onParentNodeClick(i)}
      >
        {node?.label}
      </S.ParentNode>
    ))}
  </>
);

export const ParentNodes = memo(ParentNodesComponent);
